import { ArrowLeft, LoaderCircle, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiClient,
  type AiReprocessItem,
  type AiReprocessRun
} from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { AppLink } from "@/shared/components/AppLink";
import { ApiState } from "@/shared/components/ApiState";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatDateTime } from "@/shared/utils/format";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { AiReprocessProgress } from "@/features/mentions/AiReprocessProgress";
import {
  AiReprocessChangeList,
  AiReprocessItemHead
} from "@/features/mentions/AiReprocessItemCard";
import {
  RUN_ITEM_TABS,
  describeTarget,
  diffParts,
  isAiReprocessTracked,
  runStatusMeta,
  tabCount,
  taskLabels,
  type RunItemTab
} from "@/features/mentions/ai-reprocess-view";
import { runDurationLabel } from "@/features/ai-reprocess/ReprocessRunsPage";

/** O teto do servidor (`GET /mentions/ai-reprocess/{id}/items?limit=`). */
const ITEMS_LIMIT = 500;

/**
 * Uma execução de reprocessamento por IA: o andamento ao vivo, o que foi
 * pedido, e as menções por desfecho — o que a IA mudou (com o antes →
 * depois), o que ficou igual, o que foi pulado e por quê, e as falhas.
 *
 * Cada aba pede ao servidor só os desfechos dela, e só de novo quando o
 * contador daquela aba muda: enquanto a execução anda, a lista
 * acompanha sem repetir a mesma consulta a cada volta do polling.
 */
export function ReprocessRunDetailPage({
  token,
  runId,
  backHref
}: {
  token: string;
  runId: number;
  /** Pra onde "Reprocessamentos" leva. Link, e não callback (2026-10-26). */
  backHref: string;
}) {
  const [run, setRun] = useState<AiReprocessRun | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [tab, setTab] = useState<RunItemTab>("changed");
  const [items, setItems] = useState<AiReprocessItem[] | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);
  // Descarte por CARGA: trocar de aba no meio de uma consulta não pode
  // deixar a resposta da aba anterior por cima da nova.
  const itemsLoad = useRef(0);

  const reloadRun = useCallback(async () => {
    try {
      setRun(await apiClient.getAiReprocess(token, runId));
      setLoadError(null);
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
        return;
      }
      setLoadError(
        failure instanceof ApiError && failure.status === 404
          ? "Este reprocessamento não existe ou não é desta empresa."
          : errorMessage(failure, "Não foi possível carregar o reprocessamento.")
      );
    }
  }, [token, runId]);

  useEffect(() => {
    void reloadRun();
  }, [reloadRun]);

  const tracked = run !== null && isAiReprocessTracked(run);
  usePolling({
    enabled: tracked,
    onTick: () => void reloadRun(),
    signature: run ? `${run.status}:${run.counters.pending}` : undefined,
    pausedUntil
  });

  const currentCount = run ? tabCount(run, tab) : 0;
  useEffect(() => {
    if (run === null) return;
    const load = ++itemsLoad.current;
    const outcomes = RUN_ITEM_TABS.find(item => item.key === tab)?.outcomes ?? [];
    apiClient
      .listAiReprocessItems(token, runId, { outcomes, limit: ITEMS_LIMIT })
      .then(next => {
        if (load !== itemsLoad.current) return;
        setItems(next);
        setItemsError(null);
      })
      .catch(failure => {
        if (load !== itemsLoad.current) return;
        setItemsError(errorMessage(failure, "Não foi possível carregar as menções."));
      });
    // Só refaz quando a aba muda ou o número dela muda — não a cada tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, runId, tab, currentCount, run === null]);

  if (loadError && run === null) {
    return (
      <div className="upload-detail-page">
        <header className="upload-detail-head">
          <AppLink className="secondary-button" to={backHref}>
            <ArrowLeft size={15} />Reprocessamentos
          </AppLink>
        </header>
        <ApiState kind="error" onRetry={() => void reloadRun()}>{loadError}</ApiState>
      </div>
    );
  }
  if (run === null) return <ApiState kind="loading">Carregando…</ApiState>;

  const meta = runStatusMeta(run);
  const requested = parseServerTime(run.requested_at);
  const started = parseServerTime(run.started_at);
  const finished = parseServerTime(run.finished_at);

  return (
    <div className="upload-detail-page">
      <header className="upload-detail-head">
        <AppLink className="secondary-button" to={backHref}>
          <ArrowLeft size={15} />Reprocessamentos
        </AppLink>
        <div className="upload-detail-title">
          <h2>
            {tracked ? <LoaderCircle className="spin" size={18} /> : <Sparkles size={18} />}
            Reprocessamento #{run.id}
          </h2>
          <span className="upload-detail-subtitle">{taskLabels(run)}</span>
          <span className={`upload-status ${meta.tone}`}>{meta.label}</span>
        </div>
      </header>

      <section className="card reprocess-detail-card">
        <AiReprocessProgress run={run} label={`Reprocessamento #${run.id}`} />
        <dl className="reprocess-facts">
          <div>
            <dt>Recorte</dt>
            <dd>
              {describeTarget(run)}
              {run.include_reviewed ? " · inclui revisadas" : ""}
            </dd>
          </div>
          <div>
            <dt>Pedido por</dt>
            <dd>
              {run.requested_by_name || run.requested_by}
              {requested !== null && ` em ${formatDateTime(new Date(requested))}`}
            </dd>
          </div>
          <div>
            <dt>Execução</dt>
            <dd>
              {started === null
                ? "Ainda na fila"
                : `Começou ${formatDateTime(new Date(started))}`}
              {finished !== null && !tracked && `, terminou ${formatDateTime(new Date(finished))}`}
              {runDurationLabel(run)}
            </dd>
          </div>
          {run.message && (
            <div>
              <dt>{run.status === "ERRORED" ? "Erro" : "Mensagem"}</dt>
              <dd>{run.message}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="card reprocess-detail-card">
        <div className="mode-toggle" role="tablist" aria-label="Menções por desfecho">
          {RUN_ITEM_TABS.map(item => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              className={tab === item.key ? "active" : ""}
              onClick={() => {
                setTab(item.key);
                setItems(null);
              }}
            >
              {item.label} ({tabCount(run, item.key).toLocaleString("pt-BR")})
            </button>
          ))}
        </div>
        <RunItems items={items} error={itemsError} count={currentCount} tab={tab} />
      </section>
    </div>
  );
}

function RunItems({
  items,
  error,
  count,
  tab
}: {
  items: AiReprocessItem[] | null;
  error: string | null;
  count: number;
  tab: RunItemTab;
}) {
  if (error) return <ApiState kind="error">{error}</ApiState>;
  if (items === null) return <ApiState kind="loading">Carregando…</ApiState>;
  if (items.length === 0) {
    return (
      <p className="ai-reprocess-hint">
        {tab === "changed"
          ? "Nenhuma menção alterada até agora."
          : "Nenhuma menção aqui até agora."}
      </p>
    );
  }
  return (
    <>
      {count > items.length && (
        <p className="ai-reprocess-hint">
          Mostrando {items.length.toLocaleString("pt-BR")} de{" "}
          {count.toLocaleString("pt-BR")}.
        </p>
      )}
      <ul className="ai-reprocess-sample-list">
        {items.map(item => (
          <li key={item.mention_id} className="ai-reprocess-card">
            <AiReprocessItemHead item={item} />
            <AiReprocessChangeList parts={diffParts(item.diff)} />
            {item.message && <em>{item.message}</em>}
          </li>
        ))}
      </ul>
    </>
  );
}
